// The small self-view tile during a call: draggable, and snaps to whichever
// corner it is released nearest to (BRIEF 6.3). It starts top right by default
// (Adam, 2026-10-16: "Put my own picture on breeze top right corner by
// default"), clear of the Share and "..." buttons. A tap (a press that barely
// moves and ends quickly) grows it from the corner it is in, and a second tap
// puts it back (QUEUE row 205). Enlarged, a press anywhere else on the screen
// shrinks it again. It can sit top middle too, and the top right sits to the left
// of the Share and "..." buttons, not behind them (Adam, 2026-10-04: "allow
// dragging picture to top middle and when it's dragged to top right it should
// not be behind the share and ... but rather to the left of those").
//
// A release glides from exactly where it was let go to its corner (Adam,
// 2026-10-04: "there's a rubber band effect where it briefly slides back to the
// x value from which it came"). The corner moves at once and a transform that
// starts at the drop point and runs to zero does the moving, so nothing
// interpolates between a left and a right edge or jumps back to the old one.

import { BlurLoading } from "./BlurLoading";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX } from "react";
import type { FlipPhase } from "../hooks/useCallStore";
import { HandChip } from "./HandChip";
import { VideoView, type VideoFitReport } from "./VideoView";
import { CameraOffIcon } from "./icons";

interface DraggableSelfViewProps {
  readonly stream: MediaStream | null;
  readonly cameraOn: boolean;
  /** Front camera: mirrored, like a mirror. Back camera: as the world is. */
  readonly mirrored: boolean;
  readonly flipPhase: FlipPhase;
  /** Your hand is up. */
  readonly handRaised?: boolean;
  /** The background blur is starting. */
  readonly blurLoading?: boolean;
  /** How WebKit laid out the picture, for the drops log (QUEUE row 445). */
  readonly onReport?: (report: VideoFitReport) => void;
}

const FLIP_ANIMATION: Readonly<Record<FlipPhase, string | undefined>> = {
  idle: undefined,
  out: "breeze-flip-out 170ms cubic-bezier(0.4, 0, 1, 1) forwards",
  in: "breeze-flip-in 260ms cubic-bezier(0.2, 0.9, 0.3, 1.15) both",
};

type Corner = "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-right";

const MARGIN = 16;
/** The top right clears the Share and "..." column: its width plus a gap. */
const TOP_RIGHT_CLEAR = 76;
/** A press that moves less than this and ends sooner than TAP_MS is a tap. */
const TAP_SLOP = 6;
const TAP_MS = 300;

function cornerStyle(corner: Corner): React.CSSProperties {
  const vertical = corner.startsWith("top") ? { top: MARGIN } : { bottom: MARGIN + 96 };
  if (corner === "top-center") return { ...vertical, left: "calc(50% - var(--sv-w) / 2)" };
  if (corner === "top-right") return { ...vertical, right: TOP_RIGHT_CLEAR };
  return { ...vertical, ...(corner.endsWith("left") ? { left: MARGIN } : { right: MARGIN }) };
}

/** Where a release at (x, y), as fractions of the stage, snaps to. */
function cornerAt(x: number, y: number): Corner {
  if (y < 0.5) return x < 1 / 3 ? "top-left" : x > 2 / 3 ? "top-right" : "top-center";
  return x < 0.5 ? "bottom-left" : "bottom-right";
}

export function DraggableSelfView({ stream, cameraOn, mirrored, flipPhase, handRaised, blurLoading = false, onReport }: DraggableSelfViewProps): JSX.Element {
  const [corner, setCorner] = useState<Corner>("top-right");
  const [expanded, setExpanded] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const dragStart = useRef<{ x: number; y: number; at: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  /** Where the tile was on screen when it was let go, until it has glided from there. */
  const dropRect = useRef<DOMRect | null>(null);

  useLayoutEffect(() => {
    const el = rootRef.current;
    const from = dropRect.current;
    dropRect.current = null;
    if (!el || !from) return;
    // Measured with no transition running, or the drag offset just removed
    // would start transitioning and the reading would include it.
    el.style.transition = "none";
    el.style.transform = "none";
    const to = el.getBoundingClientRect();
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    el.style.transform = `translate(${dx}px, ${dy}px)`;
    void el.offsetWidth;
    el.style.transition = "";
    el.style.transform = "";
  }, [corner, dragOffset]);

  // The tile takes the shape of the picture others receive: a desktop webcam
  // or a phone held sideways is landscape, a phone held upright is portrait
  // (Adam, 2026-10-09: "i want my picture displayed horizontal during a
  // meeting if that's what they will be seeing"). Read from the track, and
  // again after a rotation, when the browser has re-framed the camera.
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    const track = stream?.getVideoTracks()[0];
    if (!track) {
      setAspect(null);
      return;
    }
    const read = (): void => {
      const { width, height } = track.getSettings();
      setAspect(width && height ? width / height : null);
    };
    read();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const later = (): void => {
      clearTimeout(timer);
      timer = setTimeout(read, 400);
    };
    window.addEventListener("resize", later);
    window.addEventListener("orientationchange", later);
    track.addEventListener("unmute", read);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", later);
      window.removeEventListener("orientationchange", later);
      track.removeEventListener("unmute", read);
    };
  }, [stream]);
  const landscape = aspect !== null && aspect > 1;

  // Enlarged, a press anywhere else shrinks it.
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setExpanded(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [expanded]);

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    dragStart.current = { x: event.clientX, y: event.clientY, at: performance.now() };
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    if (!dragStart.current) return;
    setDragOffset({ x: event.clientX - dragStart.current.x, y: event.clientY - dragStart.current.y });
  }, []);

  const onPointerUp = useCallback((event: React.PointerEvent) => {
    const start = dragStart.current;
    dragStart.current = null;
    if (!start) return;
    dropRect.current = rootRef.current?.getBoundingClientRect() ?? null;
    setDragOffset(null);
    const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (moved < TAP_SLOP && performance.now() - start.at < TAP_MS) {
      dropRect.current = null;
      setExpanded((on) => !on);
      return;
    }
    // Judged against the stage the tile sits in, which is narrower than the
    // window when a dock is open.
    const stage = rootRef.current?.offsetParent?.getBoundingClientRect();
    const left = stage?.left ?? 0;
    const top = stage?.top ?? 0;
    const width = stage?.width ?? window.innerWidth;
    const height = stage?.height ?? window.innerHeight;
    setCorner(cornerAt((event.clientX - left) / width, (event.clientY - top) / height));
  }, []);

  const onPointerCancel = useCallback(() => {
    dragStart.current = null;
    setDragOffset(null);
  }, []);

  // Enter or Space does what a tap does, for a keyboard or a switch.
  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    setExpanded((on) => !on);
  }, []);

  const style: React.CSSProperties = {
    ...cornerStyle(corner),
    ...(aspect ? ({ "--sv-ar": String(aspect) } as React.CSSProperties) : {}),
    transform: dragOffset ? `translate(${dragOffset.x}px, ${dragOffset.y}px)` : undefined,
  };

  return (
    <div
      ref={rootRef}
      className={`selfview${landscape ? " selfview--land" : ""}${expanded ? " selfview--big" : ""}${dragOffset ? " selfview--dragging" : ""}`}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-pressed={expanded}
      aria-label={expanded ? "Your camera, enlarged" : "Your camera"}
    >
      {handRaised ? <HandChip name="You" /> : null}
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "var(--radius-md)",
          overflow: "hidden",
          boxShadow: "var(--shadow-tray)",
          border: "1.5px solid rgba(255,255,255,0.25)",
          background: "#171c22",
          animation: FLIP_ANIMATION[flipPhase],
        }}
      >
        {stream && cameraOn ? (
          <>
            <VideoView stream={stream} mirrored={mirrored} muted fit="cover" onReport={onReport} />
            <BlurLoading show={blurLoading} />
          </>
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#8a9099" }}>
            <CameraOffIcon size={expanded ? 32 : 20} />
          </div>
        )}
      </div>
    </div>
  );
}
